/**
 * WEBSOCKET / SIGNALR ADMIN SERVICE: ESQUIVADOR (SURFER)
 * Wrapper de comunicación en tiempo real para el Panel del Operador / Administrador del Estadio.
 * Gestiona la conexión persistente con SignalR Hub (`/hub/entertainment`) para recibir telemetría autoritativa,
 * sincronización de cuenta regresiva, colisiones de obstáculos, puntuaciones, estado de jugadores y ciclo de vida de la partida.
 * 
 * @module utilities/WS/Admin/SurferWS
 */

import SignalRService from '../SignalRService';

/**
 * Referencia al handler de escucha registrado en SignalRService para mensajes genéricos.
 * @type {Function|null}
 */
let registeredMessageHandler = null;

/**
 * Referencia al handler de reconexión exitosa registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectedHandler = null;

/**
 * Referencia al handler de reconexión en progreso registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectingHandler = null;

/**
 * Referencia al handler de cierre definitivo de conexión.
 * @type {Function|null}
 */
let registeredCloseHandler = null;

/**
 * Conecta el panel de administración al Hub de SignalR con el rol de operador de Esquivador ('surfer:admin').
 * 
 * Eventos clave recibidos y procesados:
 * - `surfer_cover_shown`: Portada de Esquivador proyectada en sala/pantalla gigante.
 * - `surfer_game_scene_shown` / `surfer_game_shown`: Escenario de juego preparado y proyectado.
 * - `surfer_countdown_tick`: Tick sincronizado de conteo regresivo (3, 2, 1).
 * - `surfer_match_started`: Inicio oficial de la carrera/partida de esquivador.
 * - `surfer_match_paused`: Partida pausada por el operador o evento de sistema.
 * - `surfer_match_resumed`: Partida reanudada tras pausa.
 * - `surfer_match_reset`: Partida reiniciada al estado inicial listo para jugar.
 * - `surfer_match_finished`: Proclamación de resultados finales, puntuaciones y ganador.
 * - `surfer_player_action_received`: Acción de movimiento / carril emitida por un jugador (J1 o J2).
 * - `surfer_player_hit`: Notificación de colisión contra obstáculo o pérdida de vida.
 * - `surfer_score_updated`: Actualización de puntaje, monedas y metros recorridos.
 * - `surfer_player_joined`: Notificación de participante conectado o slot ocupado.
 * - `surfer_player_left`: Notificación de desconexión o desvinculación de un participante.
 * 
 * @param {Function} [onIncomingMessage=() => {}] - Callback para procesar los mensajes y eventos de telemetría emitidos por el Hub.
 * @param {Function} [onStatusChange=() => {}] - Callback para notificar cambios de estado ('connecting', 'connected', 'reconnecting', 'error', 'closed').
 * @param {string} [roomId=''] - Identificador GUID de la sala para filtrar el tráfico de eventos.
 */
export const ConnectWebSocketForSurferAdmin = (
    onIncomingMessage = () => {},
    onStatusChange = () => {},
    roomId = ''
) => {
    onStatusChange('connecting');

    // Desuscribir handlers previos si existieran para evitar fugas de memoria o ejecuciones duplicadas
    if (registeredMessageHandler) {
        SignalRService.off('message', registeredMessageHandler);
    }
    if (registeredReconnectedHandler) {
        SignalRService.off('reconnected', registeredReconnectedHandler);
    }
    if (registeredReconnectingHandler) {
        SignalRService.off('reconnecting', registeredReconnectingHandler);
    }
    if (registeredCloseHandler) {
        SignalRService.off('close', registeredCloseHandler);
    }

    // Handler de mensajes entrantes con filtrado estricto por sala si fue provista
    registeredMessageHandler = (messageEnvelope) => {
        if (!messageEnvelope) return;

        // 1. FILTRADO ESTRICTO POR ROOM ID (AISLAMIENTO MULTI-TENANT)
        const envelopeRoomId = messageEnvelope.roomId
            || messageEnvelope.RoomId
            || messageEnvelope.payload?.roomId
            || messageEnvelope.payload?.RoomId
            || messageEnvelope.data?.roomId
            || messageEnvelope.data?.RoomId;

        if (roomId && envelopeRoomId && String(envelopeRoomId).toLowerCase() !== String(roomId).toLowerCase()) {
            return;
        }

        // 2. DESPACHO DE MENSAJE AL HANDLER GENERAL
        if (typeof onIncomingMessage === 'function') {
            try {
                onIncomingMessage(messageEnvelope);
            } catch (dispatchError) {
                console.error('[SurferAdminWS] Error en onIncomingMessage callback:', dispatchError);
            }
        }
    };

    registeredReconnectedHandler = () => {
        onStatusChange('connected');
    };

    registeredReconnectingHandler = () => {
        onStatusChange('reconnecting');
    };

    registeredCloseHandler = () => {
        onStatusChange('closed');
    };

    SignalRService.on('message', registeredMessageHandler);
    SignalRService.on('reconnected', registeredReconnectedHandler);
    SignalRService.on('reconnecting', registeredReconnectingHandler);
    SignalRService.on('close', registeredCloseHandler);

    SignalRService.connect({
        tipo: 'surfer:admin',
        roomId: roomId || ''
    })
        .then(() => {
            onStatusChange('connected');
        })
        .catch((connectionError) => {
            console.error('[SurferAdminWS] Error al establecer conexión con el Hub de SignalR:', connectionError);
            onStatusChange('error');
        });
};

/**
 * Desconecta limpiamente la sesión SignalR del operador de Esquivador y remueve los escuchas registrados.
 */
export const DisconnectWebSocketForSurferAdmin = () => {
    try {
        if (registeredMessageHandler) {
            SignalRService.off('message', registeredMessageHandler);
            registeredMessageHandler = null;
        }
        if (registeredReconnectedHandler) {
            SignalRService.off('reconnected', registeredReconnectedHandler);
            registeredReconnectedHandler = null;
        }
        if (registeredReconnectingHandler) {
            SignalRService.off('reconnecting', registeredReconnectingHandler);
            registeredReconnectingHandler = null;
        }
        if (registeredCloseHandler) {
            SignalRService.off('close', registeredCloseHandler);
            registeredCloseHandler = null;
        }
        SignalRService.disconnect();
    } catch (disconnectionError) {
        console.error('[SurferAdminWS] Error al desconectar la sesión de SignalR del operador:', disconnectionError);
    }
};

export default {
    ConnectWebSocketForSurferAdmin,
    DisconnectWebSocketForSurferAdmin
};
